import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, TextField, Button, Snackbar, Alert, Chip,
  Grid, InputAdornment, Paper, Divider
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Print, ArrowBack } from '@mui/icons-material';
import API_URL from '../../../config/api';

// Estilos para impressão
const printStyles = `
  @page {
    size: A4 portrait;
    margin: 2.5cm 2cm 2cm 3cm;
  }

  @media print {
    html, body {
      width: 210mm;
      margin: 0 !important;
      padding: 0 !important;
      background: white !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    body * {
      visibility: hidden;
    }
    #print-area, #print-area * {
      visibility: visible;
    }
    #print-area {
      position: static;
      width: 100%;
      background: white;
      padding: 0;
      margin: 0;
    }
    .no-print {
      display: none !important;
    }
    .print-header {
      display: block !important;
      text-align: center;
      margin-bottom: 24pt;
      border-bottom: 3px solid #667eea;
      padding-bottom: 16pt;
    }
    .print-logo {
      max-width: 100px;
      margin-bottom: 8pt;
    }
    .print-company-name {
      font-size: 18pt;
      font-weight: bold;
      color: #1976d2 !important;
      margin: 8pt 0;
    }
    .print-subtitle {
      color: #444 !important;
      font-size: 11pt;
      margin-top: 4pt;
    }
    .print-info-header {
      display: flex !important;
      justify-content: space-between;
      margin-bottom: 16pt;
      padding: 8pt 10pt;
      background-color: #f5f5f5 !important;
      border-radius: 4pt;
      font-size: 10pt;
    }
    .print-section {
      margin-bottom: 14pt;
      page-break-inside: avoid;
      box-shadow: none !important;
      border: 1pt solid #ddd;
      border-radius: 4pt;
      padding: 10pt 12pt;
    }
    .print-section-title {
      font-weight: bold;
      font-size: 11pt !important;
      color: #1976d2 !important;
      border-bottom: 1.5pt solid #1976d2 !important;
      padding-bottom: 5pt;
      margin-bottom: 10pt;
      text-transform: uppercase;
    }
    .print-info-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10pt;
      padding: 5pt 0;
      border-bottom: 0.5pt solid #eee;
    }
    .print-info-item {
      display: flex;
      flex-direction: column;
    }
    .print-info-label {
      font-weight: bold;
      font-size: 8pt !important;
      color: #555 !important;
      margin-bottom: 2pt;
    }
    .print-info-value {
      font-size: 10pt !important;
      color: #222 !important;
    }
    .print-total-section {
      background-color: #e7f3ff !important;
      padding: 12pt;
      border-radius: 4pt;
      border: 1.5pt solid #1976d2 !important;
      margin-top: 10pt;
    }
    .print-total-row {
      display: flex;
      justify-content: space-between;
      font-size: 13pt !important;
      font-weight: bold;
      margin: 4pt 0;
    }
    .print-footer {
      display: block !important;
      text-align: center;
      margin-top: 20pt;
      padding-top: 12pt;
      border-top: 1.5pt solid #ccc;
      color: #555 !important;
      font-size: 8pt;
      page-break-inside: avoid;
    }
    .print-status-chip {
      display: inline-block;
      padding: 2pt 8pt;
      border-radius: 10pt;
      font-size: 9pt;
      font-weight: bold;
    }
    .MuiPaper-root {
      box-shadow: none !important;
    }
    .MuiChip-root {
      border: 1pt solid #ccc;
      background: #f5f5f5 !important;
      color: #333 !important;
    }
    .MuiDivider-root {
      border-color: #ddd !important;
      margin: 8pt 0 !important;
    }
  }
`;

export default function VisualizarAluguel() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [aluguel, setAluguel] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', tipo: 'success' });

  useEffect(() => {
    const fetchAluguel = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/alugueis/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAluguel(res.data);
      } catch {
        setSnack({ open: true, msg: 'Erro ao carregar aluguel.', tipo: 'error' });
      }
    };
    fetchAluguel();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'Reservado': 'warning',
      'Em Andamento': 'info',
      'Finalizado': 'success',
      'Cancelado': 'error',
      'Pendencia': 'error',
      'Ativo': 'info',
      'Atrasado': 'error'
    };
    return statusMap[status] || 'default';
  };

  if (!aluguel) return null;

  return (
    <>
      <style>{printStyles}</style>
      <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'grid', gap: 3 }}>
            
            {/* HEADER - Visível na tela */}
            <Box className="no-print" sx={{ bgcolor: 'white', color: '#333', p: 3, borderRadius: 2, boxShadow: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
                Visualizar Aluguel #{id}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Detalhes completos do aluguel
              </Typography>
            </Box>

            {/* Área de impressão */}
            <Box id="print-area">
              {/* HEADER - Visível apenas na impressão */}
              <Box className="print-header" sx={{ display: 'none' }}>
                <img 
                  src={`${window.location.origin}/static/media/logo.88440082fa4070a3fdde.png`}
                  alt="Logo" 
                  className="print-logo"
                  onError={(e) => e.target.style.display = 'none'}
                />
                <div className="print-company-name">ATHIX Locadora de Veículos</div>
                <div className="print-subtitle">Detalhes do Aluguel</div>
              </Box>

              {/* INFO HEADER - Visível apenas na impressão */}
              <Box className="print-info-header" sx={{ display: 'none' }}>
                <div><strong>Aluguel Nº:</strong> {id}</div>
                <div><strong>Data:</strong> {new Date().toLocaleDateString('pt-BR')}</div>
                <div>
                  <strong>Status:</strong>{' '}
                  <span className="print-status-chip" style={{
                    backgroundColor: getStatusColor(aluguel.status_aluguel) === 'success' ? '#4caf50' : 
                                    getStatusColor(aluguel.status_aluguel) === 'error' ? '#f44336' :
                                    getStatusColor(aluguel.status_aluguel) === 'warning' ? '#ff9800' : '#2196f3',
                    color: 'white'
                  }}>
                    {aluguel.status_aluguel}
                  </span>
                </div>
              </Box>

              {/* SEÇÃO 1: CLIENTE */}
              <Paper elevation={2} sx={{ p: 3, mb: 3 }} className="print-section">
                <Typography className="print-section-title" variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1976d2', borderBottom: '2px solid #1976d2', pb: 1 }}>
                  DADOS DO CLIENTE
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box className="print-info-item">
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Cliente
                      </Typography>
                      <Typography className="print-info-value" variant="body1">
                        {aluguel.nome_cliente}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box className="print-info-item">
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Status de Pagamento
                      </Typography>
                      <Typography className="print-info-value" variant="body1">
                        {aluguel.status_pagamento || 'Pendente'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* SEÇÃO 2: PERÍODO DE ALUGUEL */}
              <Paper elevation={2} sx={{ p: 3, mb: 3 }} className="print-section">
                <Typography className="print-section-title" variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1976d2', borderBottom: '2px solid #1976d2', pb: 1 }}>
                  PERÍODO DE ALUGUEL
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box className="print-info-item">
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Data Retirada
                      </Typography>
                      <Typography className="print-info-value" variant="body1">
                        {new Date(aluguel.data_retirada).toLocaleDateString('pt-BR')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box className="print-info-item">
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Data Prevista Devolução
                      </Typography>
                      <Typography className="print-info-value" variant="body1">
                        {new Date(aluguel.data_devolucao).toLocaleDateString('pt-BR')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box className="print-info-item">
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Data Real Devolução
                      </Typography>
                      <Typography className="print-info-value" variant="body1">
                        {aluguel.data_real_devolucao ? new Date(aluguel.data_real_devolucao).toLocaleDateString('pt-BR') : 'Não registrada'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box className="print-info-item">
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Tipo de Período
                      </Typography>
                      <Typography className="print-info-value" variant="body1">
                        {aluguel.tipo_periodo}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box className="print-info-item">
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Quantidade de Períodos
                      </Typography>
                      <Typography className="print-info-value" variant="body1">
                        {aluguel.quantidade_periodos}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box className="print-info-item">
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Dias de Atraso
                      </Typography>
                      <Typography className="print-info-value" variant="body1" sx={{ color: aluguel.dias_atraso > 0 ? '#f44336' : 'inherit' }}>
                        {aluguel.dias_atraso || 0}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box className="print-info-item">
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Status do Aluguel
                      </Typography>
                      <Chip 
                        label={aluguel.status_aluguel}
                        color={getStatusColor(aluguel.status_aluguel)}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* SEÇÃO 3: VEÍCULO */}
              <Paper elevation={2} sx={{ p: 3, mb: 3 }} className="print-section">
                <Typography className="print-section-title" variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1976d2', borderBottom: '2px solid #1976d2', pb: 1 }}>
                  VEÍCULO
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box className="print-info-item">
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Modelo
                      </Typography>
                      <Typography className="print-info-value" variant="body1">
                        {aluguel.modelo}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box className="print-info-item">
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Placa
                      </Typography>
                      <Typography className="print-info-value" variant="body1">
                        {aluguel.placa}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box className="print-info-item">
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Proprietário
                      </Typography>
                      <Typography className="print-info-value" variant="body1">
                        {aluguel.proprietario}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* SEÇÃO 4: PROMOÇÃO */}
              {aluguel.nome_promocao && aluguel.nome_promocao !== 'Nenhuma' && (
                <Paper elevation={2} sx={{ p: 3, mb: 3 }} className="print-section">
                  <Typography className="print-section-title" variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1976d2', borderBottom: '2px solid #1976d2', pb: 1 }}>
                    PROMOÇÃO APLICADA
                  </Typography>
                  <Box className="print-info-item">
                    <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                      Promoção
                    </Typography>
                    <Typography className="print-info-value" variant="body1">
                      {aluguel.nome_promocao}
                    </Typography>
                  </Box>
                </Paper>
              )}

              {/* SEÇÃO 5: RESUMO FINANCEIRO */}
              <Paper elevation={2} sx={{ p: 3, mb: 3 }} className="print-section">
                <Typography className="print-section-title" variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1976d2', borderBottom: '2px solid #1976d2', pb: 1 }}>
                  RESUMO FINANCEIRO
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box className="print-info-item" sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Valor da Diária
                      </Typography>
                      <Typography className="print-info-value" variant="h6" sx={{ color: '#1976d2' }}>
                        R$ {parseFloat(aluguel.valor_diaria || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box className="print-info-item" sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Valor Bruto
                      </Typography>
                      <Typography className="print-info-value" variant="h6" sx={{ color: '#1976d2' }}>
                        R$ {parseFloat(aluguel.valor_bruto || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box className="print-info-item" sx={{ p: 2, bgcolor: '#fff3cd', borderRadius: 1 }}>
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Desconto Total
                      </Typography>
                      <Typography className="print-info-value" variant="h6" sx={{ color: '#ff9800' }}>
                        R$ {parseFloat(aluguel.desconto_valor || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box className="print-info-item" sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Multa por Atraso
                      </Typography>
                      <Typography className="print-info-value" variant="h6" sx={{ color: '#f44336' }}>
                        R$ {parseFloat(aluguel.valor_multa || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 2 }} />
                
                <Box className="print-total-section" sx={{ p: 3, bgcolor: '#e7f3ff', borderRadius: 2, border: '2px solid #1976d2' }}>
                  <Box className="print-total-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      VALOR TOTAL COM DESCONTO:
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      R$ {parseFloat(aluguel.valor_com_desconto || 0).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* SEÇÃO 6: PAGAMENTO */}
              <Paper elevation={2} sx={{ p: 3, mb: 3 }} className="print-section">
                <Typography className="print-section-title" variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1976d2', borderBottom: '2px solid #1976d2', pb: 1 }}>
                  FORMA DE PAGAMENTO
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box className="print-info-item">
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Forma de Pagamento
                      </Typography>
                      <Typography className="print-info-value" variant="body1">
                        {aluguel.forma_pagamento}
                      </Typography>
                    </Box>
                  </Grid>
                  {aluguel.bandeira_cartao && (
                    <Grid item xs={12} md={6}>
                      <Box className="print-info-item">
                        <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                          Bandeira Cartão
                        </Typography>
                        <Typography className="print-info-value" variant="body1">
                          {aluguel.bandeira_cartao}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Box className="print-info-item">
                      <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Pago na Retirada
                      </Typography>
                      <Chip 
                        label={aluguel.pago_na_retirada ? 'Sim' : 'Não'} 
                        color={aluguel.pago_na_retirada ? 'success' : 'warning'}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Grid>
                  {aluguel.observacoes_pagamento && (
                    <Grid item xs={12}>
                      <Box className="print-info-item">
                        <Typography className="print-info-label" variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                          Observações
                        </Typography>
                        <Typography className="print-info-value" variant="body1">
                          {aluguel.observacoes_pagamento}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* FOOTER - Visível apenas na impressão */}
              <Box className="print-footer" sx={{ display: 'none', textAlign: 'center', mt: 4, pt: 3, borderTop: '2px solid #ddd' }}>
                <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                  Este documento contém as informações completas do aluguel registrado em nosso sistema.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                  ATHIX Locadora de Veículos - Manaus/AM
                </Typography>
                <Typography variant="caption" sx={{ color: '#999' }}>
                  Impresso em: {new Date().toLocaleString('pt-BR')}
                </Typography>
              </Box>
            </Box>

            {/* BOTÕES - Não aparecem na impressão */}
            <Box className="no-print" sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/alugueis')}
                color="inherit"
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrint}
                sx={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  }
                }}
              >
                Imprimir
              </Button>
            </Box>
          </Box>
        </Container>

        <Snackbar 
          open={snack.open} 
          autoHideDuration={3000}
          onClose={() => setSnack({ ...snack, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={snack.tipo}>{snack.msg}</Alert>
        </Snackbar>
      </Box>
    </>
  );
}
